import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { navigation, siteData } from "../data";
import { cn } from "../lib/utils";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const headerSolid = scrolled || !isHome;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        headerSolid ? "bg-white py-4 shadow-sm" : "bg-transparent py-6"
      )}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img 
            src={siteData.logo} 
            alt={siteData.name} 
            className={cn(
              "transition-all duration-300 w-auto object-contain object-left",
              headerSolid ? "h-8" : "h-10"
            )}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className={cn(
          "hidden xl:flex items-center space-x-8 transition-colors duration-300 font-sans",
          headerSolid ? "text-dark" : "text-white"
        )}>
          {navigation.map((item) => (
            <div key={item.name} className="relative group">
              {item.dropdown ? (
                <div className="flex items-center space-x-1">
                  <Link
                    to={item.href}
                    className={cn(
                      "transition-colors hover:text-primary py-2 flex items-center gap-1",
                      location.pathname.startsWith(item.href) ? "text-primary font-medium" : ""
                    )}
                  >
                    {item.name}
                    <ChevronDown className="w-4 h-4" />
                  </Link>
                  <div className="absolute top-full left-0 pt-2 hidden group-hover:block">
                    <div className="bg-white shadow-lg py-2 min-w-[200px] border border-gray-100 text-dark">
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.href}
                          className="block px-4 py-2 hover:bg-gray-50 hover:text-primary transition-colors text-sm"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    "transition-colors hover:text-primary py-2",
                    location.pathname === item.href ? "text-primary font-medium" : ""
                  )}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile Toggle */}
        <button
          className={cn(
            "xl:hidden p-2 transition-colors",
            headerSolid || isOpen ? "text-dark" : "text-white"
          )}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="xl:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 max-h-[calc(100vh-5rem)] overflow-y-auto pb-10">
          <nav className="flex flex-col p-6 space-y-6">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || (item.dropdown && location.pathname.startsWith(item.href));
              
              return (
                <div key={item.name} className="flex flex-col space-y-4">
                  <Link
                    to={item.href}
                    className={cn(
                      "text-xl transition-colors",
                      isActive ? "text-primary font-medium pl-2 border-l-2 border-primary" : "text-dark"
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.dropdown && (
                    <div className="pl-4 flex flex-col space-y-4 border-l border-gray-100 ml-1">
                      {item.dropdown.map((sub) => {
                        const isSubActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.name}
                            to={sub.href}
                            className={cn(
                              "text-lg transition-colors",
                              isSubActive ? "text-primary font-medium" : "text-gray-600"
                            )}
                            onClick={() => setIsOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
